import { H5 } from "@sellar-io/components";
import { ListElement } from "../../../../components";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { InvoiceListResponseType } from "@sellar-io/api-v2/types";
import { twMerge } from "tailwind-merge";
import { formatCurrency } from "@sellar-io/common";
import { useRef } from "react";

import dayjs from "dayjs";
import { useInvoiceList } from "../../../../hooks/api/v2/invoice/useInvoiceList";
import {
  BadgeGreen,
  BadgeGrey,
  BadgeRed,
  BadgeAmber,
} from "../../../../components/badge/Badge";
import { useInvoicesFilterStore } from "../stores/InvoicesFilterStore";
import { useShallow } from "zustand/shallow";

const invoiceDisplayType = (type: string) => {
  switch (type) {
    case "openingOrderFees":
      return "OOF";
    case "sellarCreditRetailer":
    case "sellarCreditSupplier":
      return "Sellar Credit";
  }
};

function getBadgeColor(
  status: "draft" | "paid" | "void" | "issued" | "payment_processing",
  paidAt: Date | null,
  issuedAt: Date | null,
) {
  switch (status) {
    case "draft":
      return <BadgeGrey>Draft</BadgeGrey>;
    case "issued":
      return (
        <BadgeAmber>Issued: {dayjs(issuedAt).format("DD/MM/YYYY")}</BadgeAmber>
      );
    case "paid":
      return (
        <BadgeGreen>Paid: {dayjs(paidAt).format("DD/MM/YYYY")}</BadgeGreen>
      );
    case "void":
      return <BadgeRed>Void</BadgeRed>;
    case "payment_processing":
    default:
      return status;
  }
}

const InvoiceCard = ({
  invoiceItem,
  setSelectedInvoice,
  setActive,
}: {
  invoiceItem: InvoiceListResponseType[number];
  setSelectedInvoice: (Invoice: InvoiceListResponseType[number]) => void;
  setActive: (value: boolean) => void;
}) => {
  return (
    <ListElement
      onClick={() => {
        setSelectedInvoice(invoiceItem);
        setActive(true);
      }}
    >
      <div className={"flex flex-col gap-1"}>
        <span className="text-xs">
          Invoice Date:{" "}
          {invoiceItem.createdAt
            ? dayjs(invoiceItem.createdAt).format("DD/MM/YYYY")
            : "NA"}
        </span>
        <H5>
          {invoiceDisplayType(invoiceItem.type)} &ndash;&nbsp;
          {formatCurrency(invoiceItem.total)}
        </H5>
        <span className="text-sm">Invoice #{invoiceItem.id}</span>
        <div className="mt-2">
          {getBadgeColor(
            invoiceItem.status,
            invoiceItem.paidAt,
            invoiceItem.issuedAt,
          )}
        </div>
      </div>
    </ListElement>
  );
};

export const InvoicesCardList = ({
  setSelectedInvoice,
  setActive,
  search,
}: {
  setSelectedInvoice: (invoice: InvoiceListResponseType[number]) => void;
  setActive: (value: boolean) => void;
  search: string;
}) => {
  const state = useInvoicesFilterStore(useShallow((state) => state));

  const invoicesQuery = useInvoiceList({
    limit: 10,
    search,
    status: state.status && state.status !== "all" ? state.status : undefined,
    type: "openingOrderFees", // currently only show opening order fees
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      className={twMerge(
        "[scrollbar-width:none] overflow-auto border-zinc-300 [::-webkit-scrollbar]:hidden",
        // TODO: perhaps a better solution for dynamically scaling the scrollable box
        // areFilterOptionsExpanded
        //   ? "max-h-[calc(100vh-300px)]"
        //   : "max-h-[calc(100vh-220px)]",
      )}
      ref={scrollContainerRef}
    >
      <InfiniteList query={invoicesQuery}>
        {invoicesQuery.data?.pages.map((page) =>
          page.map((invoiceItem) => (
            <InvoiceCard
              invoiceItem={invoiceItem}
              key={invoiceItem.id}
              setSelectedInvoice={setSelectedInvoice}
              setActive={setActive}
            />
          )),
        )}
      </InfiniteList>
    </div>
  );
};
